'use client'

import React, { Suspense } from 'react'
import { IntlProvider } from 'react-intl'
import 'react-loading-skeleton/dist/skeleton.css'
import 'swiper/css'
import 'swiper/css/pagination'

import { HermesSdkProvider, isHermesAPIError, resendConfirmationCode } from '@hermes/api-sdk'
import { SearchProvider } from '@hermes/search'
import { useTokenValidation } from '@hooks/useTokenValidation'
import useTranslateMessage from '@hooks/useTranslateMessage'
import { AnalyticsProvider } from '@lib/contexts/analyticsContext'
import FlashMessageProvider, { useFlashMessage } from '@lib/contexts/flashMessageContext'
import { PrevRouteProvider } from '@lib/contexts/previousRouterContext'
import { UserProvider } from '@lib/contexts/userContext'
import { useAuthStore } from '@lib/stores/auth'
import { Region } from '@lib/utils/getRegionFromLocale'
import { Locale } from '@lib/utils/localeHelpers'
import { NuqsAdapter } from 'nuqs/adapters/next/app'
import { SearchToQueryProvider } from '../lib/contexts/searchToQueryContext'

interface ProvidersProps {
  children: React.ReactNode
  translations: Record<string, string>
  locale: Locale
  tenant: Region
}

const HermesSdkProviderWrapper = ({ children, tenant }: { children: React.ReactNode; tenant: Region }) => {
  const flashMessage = useFlashMessage()
  const { refresh } = useTokenValidation()
  const setAuthStep = useAuthStore((state) => state.setStep)
  const formData = useAuthStore((state) => state.formData)
  const { t } = useTranslateMessage()
  return (
    <HermesSdkProvider
      tenant={tenant}
      errorHandlers={{
        // Not desired for production
        // UnhandledAuthError: (error) => {
        //   if (isHermesAPIError(error)) {
        //     flashMessage.addFlashMessage(
        //       error.response?.data?.message || t({ id: 'error_application_fallback_message' }),
        //       'error'
        //     )
        //   } else {
        //     flashMessage.addFlashMessage(t({ id: 'error_application_fallback_message' }), 'error')
        //   }
        // },
        UserNotConfirmedError: () => {
          resendConfirmationCode({ email: formData.email })
          setAuthStep('confirmation')
          flashMessage.addFlashMessage(t({ id: 'auth_confirm_register_verify_email' }), 'warning')
        },
        UserDisabledError: () => {
          flashMessage.addFlashMessage(t({ id: 'auth_user_disabled' }), 'error')
        },
        ExpiredTokenError: async () => {
          try {
            await refresh()
          } catch (err) {
            console.error(err)
          }
        },
        ValidationError: (error) => {
          if (isHermesAPIError(error)) {
            flashMessage.addFlashMessage(error.response?.data?.message || t({ id: 'error_incorrect_login' }), 'error')
          } else {
            flashMessage.addFlashMessage(t({ id: 'error_has_occurred' }), 'error')
          }
        },
        InvalidCredentialsError: () => {
          flashMessage.addFlashMessage(t({ id: 'error_incorrect_login' }), 'error')
        },
        UserNotFoundError: () => {
          flashMessage.addFlashMessage(t({ id: 'error_user_not_found' }), 'error')
        },
        UserAlreadyExistsError: () => {},
        UserPasswordExpiredError: () => {
          flashMessage.addFlashMessage(t({ id: 'error_password_expired' }), 'warning')
          setAuthStep('resetPasswordConfirm')
        }
      }}
    >
      {children}
    </HermesSdkProvider>
  )
}

export const Providers: React.FC<ProvidersProps> = ({ children, translations, locale, tenant }) => (
  <Suspense>
    <IntlProvider
      locale={locale}
      messages={translations}
      onError={(err) => {
        if (err.code === 'MISSING_TRANSLATION' || err.code === 'MISSING_DATA') {
          console.warn('Missing translation', err.message)
          return
        }
        throw err
      }}
    >
      <NuqsAdapter>
        <PrevRouteProvider>
          <FlashMessageProvider>
            <HermesSdkProviderWrapper tenant={tenant}>
              <UserProvider locale={locale}>
                <AnalyticsProvider>
                  <SearchProvider>
                    <SearchToQueryProvider>{children}</SearchToQueryProvider>
                  </SearchProvider>
                </AnalyticsProvider>
              </UserProvider>
            </HermesSdkProviderWrapper>
          </FlashMessageProvider>
        </PrevRouteProvider>
      </NuqsAdapter>
    </IntlProvider>
  </Suspense>
)
